import { useContext, useState } from 'react';
import { AdminTableContext } from '../../AdminTable';
import { AdminFormModal, ModalStage } from '../../AdminFormModal';
import { SubmitHandler, useForm } from 'react-hook-form';
import { checkPartnerUrlParamUnique, PartnerForm, PartnerFormData } from './PartnersForm';
import { AdminPartners } from '@platform/client';
import { useQueryClient } from '@tanstack/react-query';

type UpdatePartnerModalProps = {
  partnerId: number;
  onClose: () => void;
};

export const EditPartnerModal = (props: UpdatePartnerModalProps) => {
  const { adminQueryOptionsV2 } = useContext(AdminTableContext);
  const [modalStage, setModalStage] = useState<ModalStage>('form');
  const { data: partnerData } = AdminPartners.useGetImpactPartnerUpdate(props.partnerId);
  const { ...functions } = useForm<PartnerFormData>({
    values: {
      name: partnerData?.name || '',
      logo: partnerData?.logo || '',
      description: partnerData?.description || '',
      link: partnerData?.link || '',
      urlParam: partnerData?.urlParameter || '',
      active: partnerData?.active || false,
      linkedinUrl: partnerData?.linkedinUrl || '',
    },
  });
  const queryClient = useQueryClient();
  const updatePartnerMutator = AdminPartners.useUpdatePartner({
    mutation: {
      onError: () => {
        setModalStage('error');
      },
      onMutate: () => setModalStage('submitting'),
      onSuccess: () => {
        queryClient.invalidateQueries({
          queryKey: [...AdminPartners.getGetAdminPartnersQueryKey({ ...adminQueryOptionsV2 })],
        });
        setModalStage('success');
      },
    },
  });
  const onSubmit: SubmitHandler<PartnerFormData> = async (data) => {
    if (data.urlParam) {
      const urlParamIsUnique = await checkPartnerUrlParamUnique(data.urlParam, props.partnerId);
      if (!urlParamIsUnique) {
        functions.setError('urlParam', { type: 'manual', message: 'Url param already exists' });
        return;
      } else {
        functions.clearErrors('urlParam');
      }
    }
    updatePartnerMutator.mutate({
      partnerId: props.partnerId,
      data: {
        name: data.name,
        description: data.description,
        logo: data.logo,
        link: data.link,
        active: data.active,
        urlParameter: data.urlParam ?? null,
        linkedinUrl: data.linkedinUrl ?? null,
      },
    });
  };
  return (
    <AdminFormModal
      variant='full-screen'
      title='Edit Partner'
      modalStage={modalStage}
      onClose={props.onClose}
      submitCTA={{ text: 'Save', callback: () => functions.handleSubmit(onSubmit)() }}
    >
      <PartnerForm {...functions} />
    </AdminFormModal>
  );
};
